import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Api } from "../service/Api";
import { useDataRequest } from "./api";
import { OrganizationPublicId } from "../service/server/Types";
import { OrganizationDto } from "../service/server/dto/OrganizationDto";
import { NetworkRequestState } from "../models";
import { SurveyAnswers } from "./api/useSurvey";
import { DevInstructionsPoliciesAndNoticesOnboardingSlug } from "../../polaris/pages/stay-compliant/privacy-center/DSARForms";

export type TruevaultDeveloperInstructionsOnboardingProgress = {
  isConsentManagementDevInstructionsComplete: boolean;
  isFinishedDevInstructionsSurvey: boolean;
};

export type TruevaultPrivacyCenterOnboardingProgress = {
  isFinishedWithPoliciesNotices: boolean;
  isWebsiteConsentSetup: boolean;
  hasOptInConsentBannersInCaliforniaAndFlorida: boolean;
  developerInstructionsOnboardingProgress: TruevaultDeveloperInstructionsOnboardingProgress;
  isBiometricIdentifiersPolicyFinished: boolean;
};

export const isDeveloperInstructionsOnboardingFullyComplete = (
  developerInstructions?: TruevaultDeveloperInstructionsOnboardingProgress,
) => {
  if (!developerInstructions) {
    return false;
  }

  return (
    developerInstructions.isConsentManagementDevInstructionsComplete &&
    developerInstructions.isFinishedDevInstructionsSurvey
  );
};

export type TruevaultHrOnboardingProgress = {
  isAllHrDataMapCompleted: boolean;
  isHrDataFlowsFinished: boolean;
  isFinishedWithHrRetentionPolicy: boolean;
  isFinishedWithHrDsarWorkflows: boolean;
  isHrNoticesPublished: boolean;
  isHrColoradoEmployeeBiometricNoticeComplete: boolean;
};

export const areHrNoticesComplete = (hrCenterOnboarding?: TruevaultHrOnboardingProgress): boolean =>
  hrCenterOnboarding?.isHrNoticesPublished === true &&
  hrCenterOnboarding.isHrColoradoEmployeeBiometricNoticeComplete === true;

export type TruevaultWorkflowsOnboardingProgress = {
  isFinishedWithApiIntegrationsSetup: boolean;
  isFinishedDataStorageOnboarding: boolean;
  isFinishedWorkflowSetupOnboarding: boolean;
  isLimitSettingsComplete: boolean;
  hasInvalidIntegrationCredential: boolean;
};

export type TruevaultDataMapOnboardingProgress = {
  allVendorsComplete: boolean;
  isVendorInventoryComplete: boolean;
  isDataFlowsComplete: boolean;
  isVendorDocumentationComplete: boolean;
  isDataRetentionComplete: boolean;
  /** Vendor Documents was skipped during onboarding; it stays incomplete but unlocks Data Retention. */
  isVendorDocumentationSkipped: boolean;
  /** An approved retention purpose lost all its data categories when they were removed from the data map. */
  hasRetentionPurposesWithoutCategories: boolean;
};

export type TruevaultSurveyOnboardingProgress = {
  isBusinessSurveyFinished: boolean;
  isNewJerseySensitiveDataSellingQuestionVisible: boolean;
  isHrSurveyFinished: boolean;
};

export type TruevaultOnboardingProgress = {
  privacyCenterOnboardingProgress: TruevaultPrivacyCenterOnboardingProgress;
  hrCenterOnboardingProgress: TruevaultHrOnboardingProgress;
  workflowsSetupOnboardingProgress: TruevaultWorkflowsOnboardingProgress;
  dataMapOnboardingProgress: TruevaultDataMapOnboardingProgress;
  surveyOnboardingProgress: TruevaultSurveyOnboardingProgress;
};

/*
 * Returns true if all onboarding steps are complete
 */
export const isAllOnboardingStepsComplete = (
  organization: OrganizationDto,
  onboardingProgress?: TruevaultOnboardingProgress,
) => {
  let isHrCenterSetupRequired = organization?.requiresEmployeeMapping === true;

  if (!onboardingProgress) {
    return false;
  }

  return (
    isPrivacyCenterOnboardingComplete(onboardingProgress.privacyCenterOnboardingProgress) &&
    (!isHrCenterSetupRequired ||
      isHrCenterOnboardingComplete(onboardingProgress.hrCenterOnboardingProgress)) &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const isPrivacyCenterOnboardingComplete = (
  privacyCenterOnboarding?: TruevaultPrivacyCenterOnboardingProgress,
  onlyEssentialSteps = false,
) => {
  if (!privacyCenterOnboarding) {
    return false;
  }

  return (
    privacyCenterOnboarding.isFinishedWithPoliciesNotices &&
    (onlyEssentialSteps || privacyCenterOnboarding.isWebsiteConsentSetup) &&
    isDeveloperInstructionsOnboardingFullyComplete(
      privacyCenterOnboarding.developerInstructionsOnboardingProgress,
    )
  );
};

export const didFinishPoliciesAndNoticesOnboarding = (
  developerInstructionSurveyAnswers: SurveyAnswers,
) => {
  return (
    developerInstructionSurveyAnswers &&
    developerInstructionSurveyAnswers[DevInstructionsPoliciesAndNoticesOnboardingSlug] === "true"
  );
};

export const isHrCenterOnboardingComplete = (
  hrCenterOnboarding?: TruevaultHrOnboardingProgress,
) => {
  if (!hrCenterOnboarding) {
    return false;
  }

  return (
    // Only check one-way onboarding states, so HR onboarding can't become incomplete again later after e.g. the user adds a new, still-incomplete HR vendor
    hrCenterOnboarding.isAllHrDataMapCompleted &&
    hrCenterOnboarding.isFinishedWithHrDsarWorkflows &&
    areHrNoticesComplete(hrCenterOnboarding)
  );
};

// Note that we do *not* check for isLimitSettingsComplete here, since it's not a one-time onboarding setup step
export const isWorkflowsOnboardingComplete = (
  workflowsOnboarding?: TruevaultWorkflowsOnboardingProgress,
) => {
  if (!workflowsOnboarding) {
    return false;
  }

  return (
    workflowsOnboarding.isFinishedDataStorageOnboarding &&
    workflowsOnboarding.isFinishedWithApiIntegrationsSetup &&
    workflowsOnboarding.isFinishedWorkflowSetupOnboarding
  );
};

/**
 * Returns a fake onboarding progress with all steps marked complete
 */
export const createFakeCompletedOnboardingProgress = (): TruevaultOnboardingProgress => {
  return {
    privacyCenterOnboardingProgress: {
      developerInstructionsOnboardingProgress: {
        isConsentManagementDevInstructionsComplete: true,
        isFinishedDevInstructionsSurvey: true,
      },
      isFinishedWithPoliciesNotices: true,
      isWebsiteConsentSetup: true,
      hasOptInConsentBannersInCaliforniaAndFlorida: false,
      isBiometricIdentifiersPolicyFinished: true,
    },
    hrCenterOnboardingProgress: {
      isAllHrDataMapCompleted: true,
      isHrDataFlowsFinished: true,
      isFinishedWithHrRetentionPolicy: true,
      isFinishedWithHrDsarWorkflows: true,
      isHrNoticesPublished: true,
      isHrColoradoEmployeeBiometricNoticeComplete: true,
    },
    workflowsSetupOnboardingProgress: {
      isFinishedWithApiIntegrationsSetup: true,
      isFinishedDataStorageOnboarding: true,
      isFinishedWorkflowSetupOnboarding: true,
      isLimitSettingsComplete: true,
      hasInvalidIntegrationCredential: false,
    },
    dataMapOnboardingProgress: {
      allVendorsComplete: true,
      isVendorInventoryComplete: true,
      isDataFlowsComplete: true,
      isVendorDocumentationComplete: true,
      isDataRetentionComplete: true,
      isVendorDocumentationSkipped: false,
      hasRetentionPurposesWithoutCategories: false,
    },
    surveyOnboardingProgress: {
      isBusinessSurveyFinished: true,
      isNewJerseySensitiveDataSellingQuestionVisible: false,
      isHrSurveyFinished: true,
    },
  };
};

/**
 * Targeted refresh of the onboarding-progress query (nav badges, Data Map tiles, the Data
 * Flows footer). Call from flow-level actions — footer Done/Continue clicks, dashboard
 * mounts. Purpose-category associations also invalidate after a successful save because they
 * directly change purpose completion. Avoid other per-checkbox invalidations: the progress
 * endpoint recomputes the whole data map server-side (DES-30).
 */
export const useInvalidateOnboardingProgress = (organizationId: OrganizationPublicId) => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["orgOnboardingProgress", organizationId] }),
    [queryClient, organizationId],
  );
};

export const useOnboardingProgress = (
  organizationId: OrganizationPublicId,
): [TruevaultOnboardingProgress, NetworkRequestState] => {
  const apiRequest = useCallback(
    () => Api.organization.getOrganizationOnboardingProgress(organizationId),
    [organizationId],
  );

  const { result: onboardingProgress, request: onboardingProgressRequest } = useDataRequest({
    queryKey: ["orgOnboardingProgress", organizationId],
    api: apiRequest,
    notReady: !organizationId,
  });

  return [onboardingProgress, onboardingProgressRequest];
};
